import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, Bus, CheckCircle2, ChevronRight, Clock, Eye,
  Loader2, MapPin, Maximize2, Phone, Play, Plus, RefreshCw, Search,
  Sparkles, User, UserCheck, Users, X, Wifi, WifiOff, Zap, MessageSquare,
  ListChecks, ShieldAlert, Map as MapIcon, LayoutGrid, Rows3,
  FileText, Printer, Download, Send, Calendar as CalIcon, ShieldCheck,
} from "lucide-react";
import { List as VList } from "react-window";
import LeafletMap from "@/components/LeafletMap";

// ---------------------------------------------------------------------------
// Mission Control Center — Phase A
// Dark premium UI inspired by Uber Fleet / Samsara / Optibus.
// All existing data-testids preserved; new ones added (mcc-*).
// ---------------------------------------------------------------------------

const STATUS_META = {
  pending:      { label: "EN ATTENTE",     color: "#71717A", bg: "bg-zinc-700",     ring: "ring-zinc-500",     dot: "bg-zinc-400" },
  pending_assignment: { label: "À AFFECTER", color: "#F59E0B", bg: "bg-amber-500",  ring: "ring-amber-400",    dot: "bg-amber-400" },
  active:       { label: "EN COURS",       color: "#10B981", bg: "bg-emerald-600",  ring: "ring-emerald-500",  dot: "bg-emerald-400" },
  approaching:  { label: "EN APPROCHE",    color: "#3B82F6", bg: "bg-blue-600",     ring: "ring-blue-500",     dot: "bg-blue-400" },
  delayed:      { label: "RETARD",         color: "#F59E0B", bg: "bg-orange-600",   ring: "ring-orange-500",   dot: "bg-orange-400" },
  incident:     { label: "INCIDENT",       color: "#EF4444", bg: "bg-red-600",      ring: "ring-red-500",      dot: "bg-red-400" },
  offline:      { label: "HORS LIGNE",     color: "#525252", bg: "bg-zinc-900",     ring: "ring-zinc-700",     dot: "bg-zinc-500" },
  completed:    { label: "TERMINÉE",       color: "#22C55E", bg: "bg-emerald-700",  ring: "ring-emerald-700",  dot: "bg-emerald-500" },
  ai_detected:  { label: "DÉTECTÉE PAR IA", color: "#A855F7", bg: "bg-purple-600",  ring: "ring-purple-500",   dot: "bg-purple-400" },
};

const QUICK_FILTERS = [
  { key: "all",                label: "Toutes" },
  { key: "active",             label: "Actives" },
  { key: "delayed",            label: "Retard" },
  { key: "incident",           label: "Incident" },
  { key: "pending_assignment", label: "À affecter" },
  { key: "pending",            label: "Non démarrées" },
  { key: "completed",          label: "Terminées" },
  { key: "pmr",                label: "PMR" },
  { key: "ai_detected",        label: "IA détectées" },
];

const REFRESH_MS = 5000;

export default function MissionControlCenter() {
  const [data, setData] = useState({ missions: [], kpis: {}, total: 0 });
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("compact"); // "compact" | "split" | "list" | "map" | "route-sheets"
  const [selectedRoute, setSelectedRoute] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [selectedMissionId, setSelectedMissionId] = useState(null);
  const [centerOn, setCenterOn] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: r }] = await Promise.all([
        api.get("/missions/live"),
        api.get("/routes").catch(() => ({ data: [] })),
      ]);
      setData(d);
      setRoutes(r);
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, REFRESH_MS);
    return () => clearInterval(i);
  }, [load]);

  // Realtime push: instant refresh on every mission CRUD / GPS update / incident.
  // Polling above is kept as a safety net when the WS is down.
  const wsTimer = useRef(null);
  const wsRefresh = useCallback(() => {
    if (wsTimer.current) clearTimeout(wsTimer.current);
    wsTimer.current = setTimeout(() => load(), 300);
  }, [load]);
  useRealtime(["missions", "live_vehicles", "alerts", "tour_assignments"], {
    "mission.created":   wsRefresh,
    "mission.updated":   wsRefresh,
    "mission.cancelled": wsRefresh,
    "vehicle.position":  wsRefresh,
    "incident.created":  wsRefresh,
    // Single-source: any tour_assignment mutation reloads the list so
    // mission control center reflects the current bus/driver assignment.
    "assignment.created":        wsRefresh,
    "assignment.updated":        wsRefresh,
    "assignment.deleted":        wsRefresh,
    "assignment.moved":          wsRefresh,
    "assignment.driver_changed": wsRefresh,
    "assignment.upserted":       wsRefresh,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.missions.filter((m) => {
      // filter chip
      if (filter !== "all") {
        if (filter === "pmr" && !m.is_pmr) return false;
        else if (filter !== "pmr" && m.modern_status !== filter) return false;
      }
      if (!q) return true;
      const hay = [m.route?.name, m.vehicle?.plate, m.driver?.name, m.id]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [data.missions, search, filter]);

  const counts = useMemo(() => {
    const c = { all: data.missions.length };
    QUICK_FILTERS.forEach((f) => { if (f.key !== "all") c[f.key] = 0; });
    data.missions.forEach((m) => {
      if (m.is_pmr) c.pmr = (c.pmr || 0) + 1;
      c[m.modern_status] = (c[m.modern_status] || 0) + 1;
    });
    return c;
  }, [data.missions]);

  const createMission = async () => {
    if (!selectedRoute) return toast.error("Sélectionnez une tournée");
    try {
      await api.post(`/missions?route_id=${selectedRoute}`);
      toast.success("Mission créée");
      setShowCreate(false); setSelectedRoute("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const start = async (id) => {
    try { await api.post(`/missions/${id}/start`); toast.success("Mission démarrée"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const complete = async (id) => {
    try { await api.post(`/missions/${id}/complete`); toast.success("Mission terminée"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className={`bg-zinc-50 text-zinc-900 ${fullscreen ? "fixed inset-0 z-[60] overflow-y-auto" : "min-h-[calc(100vh-0px)]"}`} data-testid="admin-missions-page">
      {/* HEADER */}
      <header className="border-b border-zinc-200 bg-white px-6 lg:px-8 py-4 sticky top-0 z-30 backdrop-blur-md">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-emerald-600 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              LIVE · auto-refresh 5s
            </div>
            <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter mt-0.5">
              Mission Control Center
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Supervision opérationnelle temps réel</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex border border-zinc-900" data-testid="mcc-view-switch">
              <button
                onClick={() => setView("compact")}
                data-testid="mcc-view-compact"
                className={`px-3 py-2 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${view === "compact" ? "bg-zinc-900 text-[#FFC60A]" : "bg-white text-zinc-700"}`}>
                <Rows3 size={12} /> Compact
              </button>
              <button
                onClick={() => setView("split")}
                data-testid="mcc-view-split"
                className={`px-3 py-2 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 border-l border-zinc-900 ${view === "split" ? "bg-zinc-900 text-[#FFC60A]" : "bg-white text-zinc-700"}`}>
                <MapIcon size={12} /> Liste + Carte
              </button>
              <button
                onClick={() => setView("list")}
                data-testid="mcc-view-list"
                className={`px-3 py-2 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 border-l border-zinc-900 ${view === "list" ? "bg-zinc-900 text-[#FFC60A]" : "bg-white text-zinc-700"}`}>
                <LayoutGrid size={12} /> Cartes
              </button>
              <button
                onClick={() => setView("map")}
                data-testid="mcc-view-map"
                className={`px-3 py-2 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 border-l border-zinc-900 ${view === "map" ? "bg-zinc-900 text-[#FFC60A]" : "bg-white text-zinc-700"}`}>
                <MapIcon size={12} /> Carte
              </button>
              <button
                onClick={() => setView("route-sheets")}
                data-testid="mcc-view-sheets"
                className={`px-3 py-2 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 border-l border-zinc-900 ${view === "route-sheets" ? "bg-zinc-900 text-[#FFC60A]" : "bg-white text-zinc-700"}`}>
                <FileText size={12} /> Fiches de route
              </button>
            </div>
            <ToolButton onClick={load} icon={loading ? null : RefreshCw} loading={loading} testId="mcc-refresh">
              Rafraîchir
            </ToolButton>
            <ToolButton onClick={() => setFullscreen(!fullscreen)} icon={Maximize2} testId="mcc-fullscreen">
              {fullscreen ? "Quitter" : "Plein écran"}
            </ToolButton>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="mcc-create-btn"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-900 hover:bg-zinc-800 transition-colors">
              <Plus size={14} /> Créer mission
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 lg:px-8 py-5 space-y-5">
        {/* KPIs */}
        <section data-testid="mcc-kpis" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-9 gap-0 border border-zinc-200 divide-x divide-zinc-200 [&>*]:p-3 bg-white shadow-sm">
          <Kpi icon={Activity} label="Actives"            value={data.kpis.missions_active ?? 0}      tone="emerald" testId="kpi-active" />
          <Kpi icon={Bus}      label="Bus"                value={data.kpis.buses_in_circulation ?? 0}  tone="yellow"  testId="kpi-buses" />
          <Kpi icon={User}     label="Chauffeurs"         value={data.kpis.drivers_active ?? 0}        tone="blue"    testId="kpi-drivers" />
          <Kpi icon={AlertTriangle} label="Incidents"     value={data.kpis.open_incidents ?? 0}        tone={data.kpis.open_incidents ? "red" : "neutral"} testId="kpi-incidents" />
          <Kpi icon={Clock}    label="Retard moyen"       value={`${data.kpis.avg_delay_min ?? 0} min`} tone={data.kpis.avg_delay_min > 3 ? "orange" : "neutral"} testId="kpi-delay" />
          <Kpi icon={MapPin}   label="Arrêts faits"       value={data.kpis.completed_stops ?? 0}       tone="neutral" testId="kpi-stops" />
          <Kpi icon={ShieldAlert} label="Bloquées"        value={data.kpis.blocked_missions ?? 0}      tone={data.kpis.blocked_missions ? "red" : "neutral"} testId="kpi-blocked" />
          <Kpi icon={AlertTriangle} label="À affecter"    value={data.kpis.pending_assignment ?? 0}    tone={data.kpis.pending_assignment ? "orange" : "neutral"} testId="kpi-pending-assignment" />
          <Kpi icon={Sparkles} label="IA détectées"       value={data.kpis.ai_detected ?? 0}           tone="purple"  testId="kpi-ai" />
        </section>

        {/* Pending-assignment alert banner */}
        {(data.kpis.pending_assignment ?? 0) > 0 && filter !== "pending_assignment" && (
          <div
            data-testid="mcc-pending-assignment-banner"
            className="border-2 border-amber-400 bg-amber-50 text-amber-900 px-4 py-2 flex items-center gap-3 shadow-[3px_3px_0_0_#F59E0B]">
            <AlertTriangle size={18} className="text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-sm tracking-tighter">
                {data.kpis.pending_assignment} mission{data.kpis.pending_assignment > 1 ? "s" : ""} sans bus ou sans chauffeur
              </div>
              <div className="text-xs">
                Affectez-les pour qu&apos;elles deviennent visibles côté chauffeur.
              </div>
            </div>
            <button
              onClick={() => setFilter("pending_assignment")}
              data-testid="mcc-pending-assignment-banner-filter"
              className="px-3 py-1.5 bg-amber-500 text-zinc-900 border border-amber-700 text-[10px] font-black uppercase tracking-widest hover:bg-amber-400">
              Voir
            </button>
            <a
              href="/admin/fleet-assignments"
              data-testid="mcc-pending-assignment-banner-link"
              className="px-3 py-1.5 bg-zinc-900 text-[#FFC60A] border border-zinc-900 text-[10px] font-black uppercase tracking-widest hover:bg-zinc-800">
              Affecter →
            </a>
          </div>
        )}

        {/* Filter & search bar */}
        <section className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap" data-testid="mcc-filters">
            {QUICK_FILTERS.map((f) => {
              const active = filter === f.key;
              const count = counts[f.key] ?? 0;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`mcc-filter-${f.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest border transition-all ${
                    active
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500 hover:text-zinc-900"
                  }`}>
                  {f.label}
                  <span className={`px-1 ${active ? "text-white/70" : "text-zinc-400"} font-bold`}>{count}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 bg-white border border-zinc-300 px-3 py-1.5 min-w-[260px]">
            <Search size={13} className="text-zinc-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Bus, chauffeur, tournée…"
              data-testid="mcc-search"
              className="flex-1 bg-transparent outline-none text-sm font-mono placeholder:text-zinc-400 text-zinc-900"
            />
          </div>
        </section>

        {error && (
          <div className="border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm" data-testid="mcc-error">
            ⚠ {error}
          </div>
        )}

        {/* Main view */}
        {view === "compact" ? (
          <CompactList missions={filtered} loading={loading} onOpen={setDrawerId} />
        ) : view === "split" ? (
          <SplitView
            missions={filtered}
            loading={loading}
            onOpen={setDrawerId}
            onStart={start}
            onComplete={complete}
            selectedId={selectedMissionId}
            setSelectedId={setSelectedMissionId}
            centerOn={centerOn}
            setCenterOn={setCenterOn}
          />
        ) : view === "map" ? (
          <LiveMap missions={filtered} onSelect={setDrawerId} />
        ) : view === "route-sheets" ? (
          <RouteSheetsView />
        ) : (
          <MissionGrid
            missions={filtered}
            loading={loading}
            onOpen={setDrawerId}
            onStart={start}
            onComplete={complete}
          />
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateMissionModal
          routes={routes}
          selectedRoute={selectedRoute}
          setSelectedRoute={setSelectedRoute}
          onClose={() => setShowCreate(false)}
          onCreate={createMission}
        />
      )}

      {/* Drawer */}
      {drawerId && (
        <MissionDrawer
          missionId={drawerId}
          onClose={() => setDrawerId(null)}
          onAction={() => { setDrawerId(null); load(); }}
          onStart={start}
          onComplete={complete}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function ToolButton({ children, icon: Icon, onClick, loading, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      disabled={loading}
      className="inline-flex items-center gap-2 bg-white text-zinc-700 border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:border-zinc-500 hover:bg-zinc-50 transition-all">
      {loading ? <Loader2 size={14} className="animate-spin" /> : Icon ? <Icon size={14} /> : null}
      {children}
    </button>
  );
}

function Kpi({ icon: Icon, label, value, tone, testId }) {
  const toneCls = {
    emerald: "text-emerald-600",
    yellow:  "text-amber-600",
    blue:    "text-blue-600",
    red:     "text-red-600",
    orange:  "text-orange-600",
    purple:  "text-purple-600",
    neutral: "text-zinc-900",
  }[tone || "neutral"];
  return (
    <div data-testid={testId} className="flex items-center justify-between gap-2 min-w-0">
      <div className="min-w-0">
        <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-zinc-500 truncate">{label}</div>
        <div className={`font-display font-black text-2xl lg:text-3xl tracking-tighter leading-none mt-1 ${toneCls}`}>{value}</div>
      </div>
      {Icon && <Icon size={16} className="text-zinc-400 shrink-0" />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function MissionGrid({ missions, loading, onOpen, onStart, onComplete, onLocate, onSelect, selectedId, columns = "1 md:grid-cols-2 xl:grid-cols-3" }) {
  if (loading && missions.length === 0) {
    return <div className="text-center text-zinc-400 py-12"><Loader2 className="animate-spin inline" /></div>;
  }
  if (missions.length === 0) {
    return (
      <div className="border border-dashed border-zinc-300 bg-white py-16 text-center" data-testid="mcc-empty">
        <Activity size={28} className="inline text-zinc-300 mb-3" />
        <div className="font-display font-bold text-base">Aucune mission ne correspond aux filtres</div>
        <div className="text-xs text-zinc-500 mt-1">Ajustez vos filtres ou créez une nouvelle mission.</div>
      </div>
    );
  }
  return (
    <div data-testid="mcc-grid" className={`grid grid-cols-${columns} gap-3`}>
      {missions.map((m) => (
        <MissionCard
          key={m.id} mission={m}
          onOpen={() => onOpen(m.id)}
          onStart={onStart} onComplete={onComplete}
          onLocate={onLocate}
          onSelect={onSelect}
          selected={selectedId === m.id} />
      ))}
    </div>
  );
}

function MissionCard({ mission: m, onOpen, onStart, onComplete, onLocate, onSelect, selected }) {
  const sm = STATUS_META[m.modern_status] || STATUS_META.pending;
  const startedAt = m.actual_start ? new Date(m.actual_start).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" }) : "—";
  const delay = m.delay_minutes || 0;
  const hasGps = !!(m.gps && typeof m.gps.lat === "number" && typeof m.gps.lng === "number");

  return (
    <div
      data-testid={`mcc-card-${m.id}`}
      data-selected={selected ? "true" : "false"}
      onClick={onSelect ? () => onSelect(m) : undefined}
      className={`bg-white border transition-all overflow-hidden ${onSelect ? "cursor-pointer" : ""} ${
        selected
          ? "border-[#FFC60A] ring-2 ring-[#FFC60A]/40 shadow-lg"
          : "border-zinc-200 hover:border-zinc-400 hover:shadow-md"
      }`}>
      {/* Status strip */}
      <div className={`px-3 py-1.5 ${sm.bg} text-white flex items-center justify-between`}>
        <span className="text-[10px] font-mono tracking-[0.3em] font-bold flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${sm.dot} ${m.modern_status === "active" || m.modern_status === "delayed" ? "animate-pulse" : ""}`} />
          {sm.label}
          {m.is_pmr && <span className="ml-1 text-[8px] bg-white/25 px-1">PMR</span>}
        </span>
        <span className="text-[10px] font-mono opacity-80">{m.date}</span>
      </div>

      {/* Body */}
      <div className="p-3 space-y-3">
        {/* Bus + driver + GPS badge */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
              <Bus size={14} className="text-amber-600" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-sm truncate">{m.vehicle?.plate || "Bus —"}</div>
              <div className="text-[10px] font-mono text-zinc-500 truncate">{m.vehicle?.model || ""}</div>
            </div>
          </div>
          <div className="text-right min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Chauffeur</div>
            <div className="text-xs font-bold truncate max-w-[120px]">{m.driver?.name || <span className="text-zinc-400">—</span>}</div>
          </div>
        </div>

        {/* GPS status badge — new */}
        <GpsStatusBadge gps={m.gps} testId={`mcc-gps-${m.id}`} />

        {/* Route */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Tournée</div>
          <div className="text-sm font-semibold truncate flex items-center gap-1.5">
            <MapPin size={11} className="text-zinc-400" />
            {m.route?.name || "—"}
          </div>
        </div>

        {/* Progression: dots + bar + tooltip with stop list */}
        <ProgressionBlock m={m} />

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-0 -mx-3 border-t border-zinc-200 divide-x divide-zinc-200">
          <Mini label="Démarrée" value={startedAt} />
          <Mini label="Retard" value={delay !== 0 ? `${delay > 0 ? "+" : ""}${delay}min` : "—"} tone={delay > 5 ? "warn" : delay > 0 ? "neutral" : "ok"} />
          <Mini label="Passagers" value={m.total_stops > 0 ? "—" : "0"} />
        </div>

        {/* Prochain arrêt — dedicated highlighted card */}
        {m.next_stop && <NextStopCard m={m} />}

        {/* Incident banner */}
        {m.open_incidents_count > 0 && (
          <div className="bg-red-50 border border-red-200 px-2.5 py-1.5 text-[11px] flex items-center gap-2">
            <AlertTriangle size={12} className="text-red-500 shrink-0" />
            <span className="text-red-700">{m.open_incidents_count} incident{m.open_incidents_count > 1 ? "s" : ""} ouvert{m.open_incidents_count > 1 ? "s" : ""}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className={`border-t border-zinc-200 grid ${onLocate && hasGps ? "grid-cols-4" : "grid-cols-3"} divide-x divide-zinc-200`}>
        <CardAction icon={Eye}      label="Détails"  onClick={onOpen} testId={`mcc-open-${m.id}`} />
        {m.status === "pending" ? (
          <CardAction icon={Play}        label="Démarrer" onClick={() => onStart(m.id)} testId={`mcc-start-${m.id}`} highlight />
        ) : m.status === "active" ? (
          <CardAction icon={CheckCircle2} label="Terminer" onClick={() => onComplete(m.id)} testId={`mcc-complete-${m.id}`} />
        ) : (
          <CardAction icon={ListChecks}  label="Historique" onClick={onOpen} testId={`mcc-history-${m.id}`} />
        )}
        {m.driver?.phone ? (
          <CardAction icon={Phone}   label="Appeler"  href={`tel:${m.driver.phone}`} testId={`mcc-call-${m.id}`} />
        ) : (
          <CardAction icon={MessageSquare} label="Notes" onClick={onOpen} testId={`mcc-notes-${m.id}`} />
        )}
        {onLocate && hasGps && (
          <CardAction icon={MapPin} label="Localiser" onClick={() => onLocate(m)} testId={`mcc-locate-${m.id}`} />
        )}
      </div>
    </div>
  );
}

/* ── GPS status badge ─────────────────────────────────────────────────
   🟢 LIVE (<60s) · 🟡 1-5 min · 🟠 5-30 min · 🔴 > 30 min / offline
   ──────────────────────────────────────────────────────────────────── */
function GpsStatusBadge({ gps, testId }) {
  if (!gps || (typeof gps.lat !== "number" && typeof gps.lng !== "number")) {
    return (
      <div data-testid={testId} className="bg-zinc-900 text-zinc-300 border border-zinc-900 px-2.5 py-1.5 flex items-center gap-2">
        <WifiOff size={12} className="text-red-400" />
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-red-400 font-bold">GPS HORS LIGNE</div>
          <div className="text-[10px] text-zinc-500 font-mono">Aucune position connue</div>
        </div>
      </div>
    );
  }
  const ageS = gps.age_seconds ?? 0;
  let tone, label, sub;
  if (ageS < 60) {
    tone = { wrap: "bg-emerald-50 border-emerald-300", text: "text-emerald-700", icon: "text-emerald-600", pulse: "bg-emerald-500" };
    label = "GPS LIVE";
    sub = "À l'instant";
  } else if (ageS < 5 * 60) {
    tone = { wrap: "bg-amber-50 border-amber-300", text: "text-amber-700", icon: "text-amber-600", pulse: "bg-amber-500" };
    label = `GPS ${Math.round(ageS / 60)} min`;
    sub = `Il y a ${Math.round(ageS / 60)} min`;
  } else if (ageS < 30 * 60) {
    tone = { wrap: "bg-orange-50 border-orange-300", text: "text-orange-700", icon: "text-orange-600", pulse: "bg-orange-500" };
    label = `GPS ${Math.round(ageS / 60)} min`;
    sub = `Il y a ${Math.round(ageS / 60)} min`;
  } else {
    tone = { wrap: "bg-red-50 border-red-300", text: "text-red-700", icon: "text-red-600", pulse: "bg-red-500" };
    label = "GPS HORS LIGNE";
    sub = ageS > 3600 ? `Il y a ${Math.round(ageS / 3600)} h` : `Il y a ${Math.round(ageS / 60)} min`;
  }
  const speed = typeof gps.speed_kmh === "number" ? `${Math.round(gps.speed_kmh)} km/h` : "—";
  return (
    <div data-testid={testId} className={`${tone.wrap} border px-2.5 py-1.5 flex items-center gap-2`}>
      <div className="relative">
        <Wifi size={12} className={tone.icon} />
        {ageS < 60 && <span className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ${tone.pulse} animate-pulse`} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] font-mono uppercase tracking-widest ${tone.text} font-bold`}>{label}</div>
        <div className="text-[10px] text-zinc-600 font-mono truncate">{sub} · {speed}</div>
      </div>
    </div>
  );
}

/* ── Progression block — dots + bar + hover stop list ────────────────── */
function ProgressionBlock({ m }) {
  const total = m.total_stops || 0;
  const done = m.completed_stops || 0;
  const pct = Math.round(m.progress_percent || 0);
  const stops = m.stops_summary || [];

  // Render up to 12 dots inline; collapse to a textual "X/Y" beyond that.
  const dotsLimit = 12;
  const showDots = total > 0 && total <= dotsLimit;
  return (
    <div className="relative group/prog">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest mb-1">
        <span className="text-zinc-500">Progression</span>
        <span className="text-zinc-800 font-bold">{done}/{total} · {pct}%</span>
      </div>
      {showDots && (
        <div className="flex items-center gap-1 mb-1" data-testid={`mcc-dots-${m.id}`}>
          {Array.from({ length: total }).map((_, i) => {
            const completed = i < done;
            const isNext = i === done && m.status === "active";
            return (
              <span
                key={i}
                title={stops[i]?.address || ""}
                className={`w-2.5 h-2.5 rounded-full border ${
                  completed ? "bg-emerald-500 border-emerald-600"
                  : isNext  ? "bg-amber-400 border-amber-600 animate-pulse"
                            : "bg-white border-zinc-300"
                }`}
              />
            );
          })}
        </div>
      )}
      <div className="h-2 bg-zinc-100 border border-zinc-200">
        <div
          className={`h-full transition-all duration-500 ${
            m.modern_status === "delayed" ? "bg-orange-500"
            : m.modern_status === "incident" ? "bg-red-500"
            : m.modern_status === "completed" ? "bg-emerald-600"
            : "bg-emerald-500"
          }`}
          style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {/* Hover tooltip with stop list */}
      {stops.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-20 hidden group-hover/prog:block">
          <div className="bg-zinc-900 text-white border border-zinc-900 shadow-[3px_3px_0_0_#FFC60A] p-2 max-h-56 overflow-y-auto" data-testid={`mcc-stops-tooltip-${m.id}`}>
            <div className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#FFC60A] mb-1">Arrêts</div>
            <ul className="space-y-0.5 text-[11px]">
              {stops.map((s, i) => (
                <li key={i} className={`flex items-start gap-1.5 ${s.completed ? "text-zinc-400 line-through" : s.is_next ? "text-[#FFC60A] font-bold" : "text-zinc-200"}`}>
                  <span className="shrink-0 mt-0.5">{s.completed ? "✓" : s.is_next ? "►" : "○"}</span>
                  <span className="flex-1 truncate">{s.address}</span>
                  {s.scheduled_time && <span className="text-[10px] text-zinc-500 font-mono shrink-0">{String(s.scheduled_time).slice(0, 5)}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Next-stop dedicated card ─────────────────────────────────────────── */
function NextStopCard({ m }) {
  const delay = m.delay_minutes || 0;
  const tone = delay > 5
    ? { wrap: "bg-red-50 border-red-300 border-l-4", text: "text-red-800", strong: "text-red-700" }
    : delay > 0
    ? { wrap: "bg-amber-50 border-amber-300 border-l-4", text: "text-amber-800", strong: "text-amber-700" }
    : { wrap: "bg-emerald-50 border-emerald-300 border-l-4", text: "text-emerald-800", strong: "text-emerald-700" };
  // Best-effort distance to next stop (only if we have both GPS & next stop coords)
  let distKm = null;
  if (m.gps?.lat && m.gps?.lng && m.next_stop?.lat && m.next_stop?.lng) {
    const R = 6371;
    const dLat = (m.next_stop.lat - m.gps.lat) * Math.PI / 180;
    const dLng = (m.next_stop.lng - m.gps.lng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(m.gps.lat * Math.PI / 180) * Math.cos(m.next_stop.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    distKm = (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(1);
  }
  return (
    <div className={`${tone.wrap} px-2.5 py-2`} data-testid={`mcc-next-${m.id}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <MapPin size={12} className={tone.strong} />
        <span className={`text-[9px] font-mono uppercase tracking-[0.3em] font-bold ${tone.strong}`}>Prochain arrêt</span>
      </div>
      <div className={`text-sm font-bold truncate ${tone.text}`}>{m.next_stop.address}</div>
      <div className="grid grid-cols-3 gap-2 mt-1.5">
        <div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">ETA</div>
          <div className={`text-xs font-bold ${tone.strong}`}>{m.next_stop.scheduled_time ? String(m.next_stop.scheduled_time).slice(0, 5) : "—"}</div>
        </div>
        <div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Distance</div>
          <div className={`text-xs font-bold ${tone.text}`}>{distKm ? `${distKm} km` : "—"}</div>
        </div>
        <div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Retard</div>
          <div className={`text-xs font-bold ${tone.strong}`}>{delay !== 0 ? `${delay > 0 ? "+" : ""}${delay} min` : "À l'heure"}</div>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, tone }) {
  const cls = tone === "warn" ? "text-orange-600" : tone === "ok" ? "text-emerald-600" : "text-zinc-800";
  return (
    <div className="px-3 py-2 text-center">
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-xs font-bold mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

function CardAction({ icon: Icon, label, onClick, href, testId, highlight }) {
  const cls = `px-2 py-2 text-[10px] font-mono uppercase tracking-widest flex items-center justify-center gap-1.5 transition-colors ${
    highlight ? "bg-emerald-600 text-white hover:bg-emerald-500" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
  }`;
  const stop = (e) => e.stopPropagation();
  if (href) {
    return <a href={href} onClick={stop} className={cls} data-testid={testId}><Icon size={11} /> {label}</a>;
  }
  return <button onClick={(e) => { e.stopPropagation(); onClick?.(e); }} data-testid={testId} className={cls}><Icon size={11} /> {label}</button>;
}

// ────────────────────────────────────────────────────────────────────────────
// CompactList — virtualized 1-row-per-mission listing (max 80 px tall).
// Built to scale to 100-500 missions: only visible rows are rendered.
// Click on a row → opens the mission drawer (which lazy-loads the map).
// ────────────────────────────────────────────────────────────────────────────
const COMPACT_ROW_HEIGHT = 80;

function CompactList({ missions, loading, onOpen }) {
  if (loading && missions.length === 0) {
    return <div className="text-center text-zinc-400 py-12"><Loader2 className="animate-spin inline" /></div>;
  }
  if (missions.length === 0) {
    return (
      <div className="border border-dashed border-zinc-300 bg-white py-16 text-center" data-testid="mcc-empty">
        <Activity size={28} className="inline text-zinc-300 mb-3" />
        <div className="font-display font-bold text-base">Aucune mission ne correspond aux filtres</div>
        <div className="text-xs text-zinc-500 mt-1">Ajustez vos filtres ou créez une nouvelle mission.</div>
      </div>
    );
  }

  const useVirtual = missions.length > 18;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;
  const listH = Math.min(missions.length * COMPACT_ROW_HEIGHT, Math.max(viewportH - 280, 420));

  return (
    <div className="bg-white border border-zinc-200" data-testid="mcc-compact">
      <div className="grid grid-cols-[8px_auto_1fr_1fr_1.4fr_1.4fr_auto] gap-2 items-center px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[9px] font-mono uppercase tracking-[0.3em] text-zinc-500">
        <div />
        <div className="w-24">Statut</div>
        <div className="hidden md:block">Bus</div>
        <div className="hidden md:block">Chauffeur</div>
        <div>Tournée · Progression</div>
        <div className="hidden lg:block">Prochain arrêt</div>
        <div className="w-24 text-right">Retard</div>
      </div>

      {useVirtual ? (
        <VList
          rowComponent={CompactVRow}
          rowCount={missions.length}
          rowHeight={COMPACT_ROW_HEIGHT}
          rowProps={{ missions, onOpen }}
          defaultHeight={listH}
          style={{ height: listH }}
        />
      ) : (
        <ul data-testid="mcc-compact-rows">
          {missions.map((m, i) => (
            <li key={m.id}>
              <CompactRow mission={m} onOpen={onOpen} isLast={i === missions.length - 1} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CompactVRow({ index, style, ariaAttributes, missions, onOpen }) {
  const m = missions[index];
  return (
    <div style={style} {...ariaAttributes}>
      <CompactRow mission={m} onOpen={onOpen} isLast={index === missions.length - 1} />
    </div>
  );
}

function CompactRow({ mission: m, onOpen, isLast }) {
  const sm = STATUS_META[m.modern_status] || STATUS_META.pending;
  const delay = m.delay_minutes || 0;
  const pct = Math.round(m.progress_percent || 0);
  const isLive = m.gps?.age_seconds != null && m.gps.age_seconds < 60;
  const isPendingAssignment = m.modern_status === "pending_assignment";
  const delayTone =
    delay > 5 ? "text-red-600"   : delay > 0 ? "text-amber-600" : delay < 0 ? "text-sky-600" : "text-emerald-600";

  return (
    <button
      onClick={() => onOpen(m.id)}
      data-testid={`mcc-row-${m.id}`}
      data-pending-assignment={isPendingAssignment ? "1" : undefined}
      className={`w-full text-left h-[80px] grid grid-cols-[8px_auto_1fr_1fr_1.4fr_1.4fr_auto] gap-2 items-center px-3 ${isLast ? "" : "border-b border-zinc-100"} ${isPendingAssignment ? "bg-amber-50/70 hover:bg-amber-50" : "hover:bg-zinc-50"} transition-colors`}>
      <div
        className={`h-full w-1.5 ${sm.bg}`}
        style={isPendingAssignment ? {
          backgroundImage: "repeating-linear-gradient(45deg, #F59E0B, #F59E0B 4px, #FCD34D 4px, #FCD34D 8px)",
        } : undefined}
        aria-hidden />
      <div className="w-24">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-widest text-white ${sm.bg} ${isPendingAssignment ? "animate-pulse" : ""}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${sm.dot} ${m.modern_status === "active" || m.modern_status === "delayed" ? "animate-pulse" : ""}`} />
          {sm.label.split(" ")[0]}
        </span>
        {m.is_pmr && <span className="ml-1 text-[8px] bg-amber-100 text-amber-700 border border-amber-200 px-1">PMR</span>}
      </div>
      <div className="hidden md:flex items-center gap-1.5 min-w-0">
        <Bus size={12} className="text-zinc-400 shrink-0" />
        <span className="font-display font-bold text-sm truncate">{m.vehicle?.plate || "—"}</span>
        <span
          className={`w-1.5 h-1.5 rounded-full shrink-0 ${
            isLive ? "bg-emerald-500" : m.gps?.lat ? "bg-amber-500" : "bg-zinc-300"
          }`}
          title={isLive ? "GPS live" : m.gps?.lat ? `GPS ${Math.round((m.gps.age_seconds || 0) / 60)} min` : "GPS hors ligne"}
          data-testid={`mcc-row-gps-${m.id}`}
        />
      </div>
      <div className="hidden md:block min-w-0">
        <div className="text-xs font-bold truncate">{m.driver?.name || <span className="text-zinc-400">—</span>}</div>
        {m.driver?.phone && <div className="text-[10px] font-mono text-zinc-400 truncate">{m.driver.phone}</div>}
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold truncate flex items-center gap-1">
          <MapPin size={10} className="text-zinc-400 shrink-0" />
          <span className="truncate">{m.route?.name || "—"}</span>
        </div>
        <div className="mt-1 flex items-center gap-2" data-testid={`mcc-row-progress-${m.id}`}>
          <div className="flex-1 h-1.5 bg-zinc-100 border border-zinc-200">
            <div
              className={`h-full transition-all ${
                m.modern_status === "delayed" ? "bg-orange-500"
                : m.modern_status === "incident" ? "bg-red-500"
                : m.modern_status === "completed" ? "bg-emerald-600"
                : "bg-emerald-500"
              }`}
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
          <span className="text-[10px] font-mono text-zinc-500 tabular-nums w-12 text-right">
            {m.completed_stops}/{m.total_stops}
          </span>
        </div>
      </div>
      <div className="hidden lg:block min-w-0">
        {m.next_stop ? (
          <>
            <div className="text-xs truncate">{m.next_stop.address}</div>
            <div className="text-[10px] font-mono text-zinc-500">
              ETA {m.next_stop.scheduled_time ? String(m.next_stop.scheduled_time).slice(0, 5) : "—"}
            </div>
          </>
        ) : (
          <div className="text-[10px] font-mono text-zinc-400">—</div>
        )}
      </div>
      <div className="w-24 text-right">
        <div className={`text-sm font-display font-black tabular-nums ${delayTone}`}>
          {delay !== 0 ? `${delay > 0 ? "+" : ""}${delay}min` : "À l'heure"}
        </div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-400 inline-flex items-center gap-0.5 justify-end w-full">
          Ouvrir <ChevronRight size={9} />
        </div>
      </div>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function SplitView({ missions, loading, onOpen, onStart, onComplete, selectedId, setSelectedId, centerOn, setCenterOn }) {
  // Build LeafletMap vehicles array from missions with GPS
  const vehicles = useMemo(() => missions
    .filter((m) => m.gps?.lat && m.gps?.lng)
    .map((m) => {
      const delay = m.delay_minutes || 0;
      const status = delay >= 10 ? "CRITICAL"
                   : delay >= 5  ? "LATE_3_MIN"
                   : delay >= 1  ? "LATE_1_MIN"
                   : "ON_TIME";
      return {
        vehicle_id: m.id, // we key markers by mission id here (1 bus = 1 active mission)
        navixy_tracker_id: m.vehicle?.navixy_tracker_id,
        plate: m.vehicle?.plate,
        label: m.vehicle?.plate,
        route_short_name: m.route?.name,
        driver_name: m.driver?.name,
        lat: m.gps.lat, lon: m.gps.lng,
        heading: m.gps.heading,
        speed: m.gps.speed_kmh,
        last_seen: m.gps.updated_at,
        delay_minutes: delay,
        next_stop_name: m.next_stop?.address,
        scheduled_next_arrival: m.next_stop?.scheduled_time,
        status,
        gps_source: "real",
      };
    }), [missions]);

  // Auto-scroll selected card into view in the left pane
  const listRef = useRef(null);
  useEffect(() => {
    if (!selectedId) return;
    const el = listRef.current?.querySelector(`[data-testid="mcc-card-${selectedId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedId]);

  const onLocate = useCallback((m) => {
    if (!m.gps?.lat || !m.gps?.lng) return;
    setSelectedId(m.id);
    setCenterOn({ lat: m.gps.lat, lng: m.gps.lng, zoom: 17, nonce: Date.now() });
  }, [setSelectedId, setCenterOn]);

  // Selecting a card highlights it; if it has GPS, the map auto-pans (no zoom-in,
  // just centering). This lets dispatchers click any card and see the bus.
  const onSelect = useCallback((m) => {
    setSelectedId(m.id);
    if (m.gps?.lat && m.gps?.lng) {
      setCenterOn({ lat: m.gps.lat, lng: m.gps.lng, zoom: 15, nonce: Date.now() });
    }
  }, [setSelectedId, setCenterOn]);

  const onVehicleClick = useCallback((v) => {
    setSelectedId(v.vehicle_id);
    setCenterOn({ lat: v.lat, lng: v.lon, zoom: 16, nonce: Date.now() });
  }, [setSelectedId, setCenterOn]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[55%_45%] gap-3" data-testid="mcc-split">
      {/* Left — mission list (compact 1-col) */}
      <div ref={listRef} className="lg:max-h-[calc(100vh-280px)] lg:overflow-y-auto pr-1" data-testid="mcc-split-list">
        <MissionGrid
          missions={missions}
          loading={loading}
          onOpen={onOpen}
          onStart={onStart}
          onComplete={onComplete}
          onLocate={onLocate}
          onSelect={onSelect}
          selectedId={selectedId}
          columns="1"
        />
      </div>
      {/* Right — live map */}
      <div className="lg:sticky lg:top-[180px] lg:self-start" data-testid="mcc-split-map">
        <LeafletMap
          waypoints={[]}
          vehicles={vehicles}
          selectedVehicleId={selectedId}
          onVehicleClick={onVehicleClick}
          centerOn={centerOn}
          height={Math.max(520, Math.min(720, (typeof window !== "undefined" ? window.innerHeight : 800) - 280))}
          showPath={false}
          emptyMessage={vehicles.length === 0 ? "Aucun bus en mission active avec GPS" : null}
          testId="mcc-split-leaflet"
        />
        <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-3">
          <span><b className="text-zinc-900">{vehicles.length}</b> bus avec GPS</span>
          <span className="text-zinc-300">·</span>
          <span>Cliquez un bus pour ouvrir sa mission</span>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function LiveMap({ missions, onSelect }) {
  // Build waypoints with bus icon labels. Color by status.
  const waypoints = missions
    .filter((m) => m.gps?.lat && m.gps?.lng)
    .map((m) => ({
      lat: m.gps.lat,
      lng: m.gps.lng,
      address: `${m.vehicle?.plate || "Bus"} · ${m.route?.name || ""} · ${STATUS_META[m.modern_status]?.label || ""}`,
      _mid: m.id,
    }));

  return (
    <div className="border border-zinc-200 bg-white overflow-hidden" data-testid="mcc-map">
      <LeafletMap waypoints={waypoints.length ? waypoints : [{ lat: 46.5083, lng: 6.4977, address: "Aucune position GPS active" }]} height={520} />
      <div className="bg-zinc-50 border-t border-zinc-200 p-3 grid sm:grid-cols-3 gap-2">
        {missions.filter((m) => m.gps?.lat).slice(0, 6).map((m) => {
          const sm = STATUS_META[m.modern_status] || STATUS_META.pending;
          return (
            <button
              key={m.id}
              onClick={() => onSelect(m.id)}
              className="text-left bg-white border border-zinc-200 p-2 hover:border-zinc-400 transition-colors"
              data-testid={`mcc-mapcard-${m.id}`}>
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} />
                <span className="font-bold text-xs">{m.vehicle?.plate || "—"}</span>
                <span className="text-[9px] font-mono text-zinc-500 ml-auto">{sm.label}</span>
              </div>
              <div className="text-[10px] font-mono text-zinc-600 truncate mt-0.5">{m.route?.name}</div>
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                {m.completed_stops}/{m.total_stops} arrêts · {m.delay_minutes > 0 ? `+${m.delay_minutes}min` : "—"}
              </div>
            </button>
          );
        })}
        {missions.filter((m) => m.gps?.lat).length === 0 && (
          <div className="col-span-3 text-center text-zinc-500 text-xs py-3">Aucune mission avec GPS actif</div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function CreateMissionModal({ routes, selectedRoute, setSelectedRoute, onClose, onCreate }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mcc-create-modal">
      <div className="bg-white border border-zinc-300 w-full max-w-md shadow-2xl">
        <div className="border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-emerald-600">Mission Control</div>
            <div className="font-display font-black text-xl tracking-tighter">Créer une mission</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Tournée *</label>
          <select
            value={selectedRoute}
            onChange={(e) => setSelectedRoute(e.target.value)}
            data-testid="mission-route-select"
            className="w-full bg-white border border-zinc-300 px-3 py-2 text-sm font-mono">
            <option value="">— Choisir une tournée —</option>
            {routes.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.date})</option>)}
          </select>
          <div className="text-[10px] font-mono text-zinc-600 bg-zinc-50 border border-zinc-200 p-2.5">
            ⓘ Un bus et chauffeur seront automatiquement affectés depuis la tournée si non-définis.
          </div>
        </div>
        <div className="border-t border-zinc-200 px-4 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900 px-3 py-2">
            Annuler
          </button>
          <button
            onClick={onCreate}
            data-testid="create-mission-btn"
            className="bg-zinc-900 text-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800">
            Créer la mission
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// DrawerMap — lightweight map shown inside the mission drawer.
// Mounted only while the drawer is open (lazy by virtue of the drawer being
// conditional on `drawerId`). Includes a "Localiser" button that flies to the
// bus position and a "GPS LIVE / X min / OFFLINE" badge.
// ────────────────────────────────────────────────────────────────────────────
function DrawerMap({ mission }) {
  const [centerOn, setCenterOn] = useState(null);
  const gp = mission.gps_position;
  const hasGps = !!(gp && typeof gp.lat === "number" && typeof gp.lng === "number");

  const vehicles = useMemo(() => {
    if (!hasGps) return [];
    const delay = mission.delay_minutes || 0;
    const status = delay >= 10 ? "CRITICAL"
                 : delay >= 5  ? "LATE_3_MIN"
                 : delay >= 1  ? "LATE_1_MIN"
                 : "ON_TIME";
    return [{
      vehicle_id: mission.id,
      navixy_tracker_id: mission.vehicle?.navixy_tracker_id,
      plate: mission.vehicle?.plate,
      label: mission.vehicle?.plate,
      route_short_name: mission.route?.name,
      driver_name: mission.driver?.name,
      lat: gp.lat, lon: gp.lng,
      heading: gp.heading,
      speed: gp.speed_kmh,
      last_seen: gp.updated_at,
      delay_minutes: delay,
      next_stop_name: mission.next_stop?.address,
      scheduled_next_arrival: mission.next_stop?.scheduled_time,
      status,
      gps_source: "real",
    }];
  }, [mission, hasGps, gp]);

  const ageS = gp?.age_seconds ?? null;
  let gpsBadge;
  if (!hasGps) {
    gpsBadge = { label: "GPS HORS LIGNE", cls: "bg-red-100 text-red-700 border-red-300", icon: WifiOff, dot: "bg-red-500" };
  } else if (ageS != null && ageS < 60) {
    gpsBadge = { label: "GPS LIVE", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: Wifi, dot: "bg-emerald-500" };
  } else if (ageS != null && ageS < 300) {
    gpsBadge = { label: `GPS ${Math.round(ageS / 60)} min`, cls: "bg-amber-100 text-amber-700 border-amber-300", icon: Wifi, dot: "bg-amber-500" };
  } else {
    gpsBadge = { label: `GPS ${Math.round((ageS || 0) / 60)} min`, cls: "bg-orange-100 text-orange-700 border-orange-300", icon: Wifi, dot: "bg-orange-500" };
  }
  const Icon = gpsBadge.icon;

  return (
    <div className="bg-white border border-zinc-200" data-testid="drawer-map-block">
      <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-zinc-200 bg-zinc-50">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 font-bold inline-flex items-center gap-1.5">
          <MapIcon size={11} /> Carte temps réel
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-widest border ${gpsBadge.cls}`} data-testid="drawer-gps-badge">
            <span className={`w-1.5 h-1.5 rounded-full ${gpsBadge.dot} ${ageS != null && ageS < 60 ? "animate-pulse" : ""}`} />
            <Icon size={10} /> {gpsBadge.label}
          </span>
          <button
            onClick={() => hasGps && setCenterOn({ lat: gp.lat, lng: gp.lng, zoom: 17, nonce: Date.now() })}
            disabled={!hasGps}
            data-testid="drawer-locate"
            className="text-[10px] font-mono uppercase tracking-widest border border-zinc-900 bg-[#FFC60A] text-zinc-900 px-2 py-0.5 hover:bg-zinc-900 hover:text-[#FFC60A] disabled:opacity-40 inline-flex items-center gap-1">
            <MapPin size={10} /> Localiser
          </button>
        </div>
      </div>
      <LeafletMap
        waypoints={[]}
        vehicles={vehicles}
        selectedVehicleId={hasGps ? mission.id : null}
        centerOn={centerOn}
        height={260}
        showPath={false}
        emptyMessage={hasGps ? null : "Aucune position GPS pour ce véhicule"}
        testId="drawer-leaflet"
      />
      {hasGps && (
        <div className="px-3 py-1.5 grid grid-cols-3 gap-2 text-[10px] font-mono text-zinc-600 border-t border-zinc-200 bg-zinc-50">
          <div><span className="text-zinc-400 uppercase tracking-widest">Vitesse</span><br /><b className="text-zinc-900">{Math.round(gp.speed_kmh ?? 0)} km/h</b></div>
          <div><span className="text-zinc-400 uppercase tracking-widest">Cap</span><br /><b className="text-zinc-900">{Math.round(gp.heading ?? 0)}°</b></div>
          <div><span className="text-zinc-400 uppercase tracking-widest">Maj GPS</span><br /><b className="text-zinc-900">{gp.updated_at?.slice(11, 19) || "—"}</b></div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function MissionDrawer({ missionId, onClose, onAction, onStart, onComplete }) {
  const [mission, setMission] = useState(null);
  const [timeline, setTimeline] = useState({ items: [] });
  const [incidents, setIncidents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tab, setTab] = useState("overview");
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: t }, { data: i }, { data: n }] = await Promise.all([
        api.get(`/missions/${missionId}`),
        api.get(`/missions/${missionId}/timeline`),
        api.get(`/missions/${missionId}/incidents`),
        api.get(`/missions/${missionId}/dispatch-notes`),
      ]);
      setMission(m); setTimeline(t); setIncidents(i); setNotes(n);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [missionId]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const submitNote = async () => {
    if (!noteDraft.trim()) return;
    try {
      await api.post(`/missions/${missionId}/dispatch-note`, { body: noteDraft.trim() });
      setNoteDraft(""); toast.success("Note ajoutée"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!mission) return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full text-center pt-20 text-zinc-500"><Loader2 className="animate-spin inline" /> Chargement…</div>
    </div>
  );

  const sm = STATUS_META[mission.modern_status || mission.status] || STATUS_META.pending;
  const route = mission.route;
  const veh = mission.vehicle;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="mcc-drawer">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="w-full max-w-xl bg-white border-l border-zinc-200 overflow-y-auto text-zinc-900 shadow-2xl">
        {/* Drawer header */}
        <div className={`px-4 py-3 ${sm.bg} text-white flex items-center justify-between sticky top-0 z-10`}>
          <div className="min-w-0">
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase font-bold opacity-90">● {sm.label}</div>
            <div className="font-display font-black text-lg tracking-tighter truncate">{route?.name || "Mission"}</div>
          </div>
          <button onClick={onClose} data-testid="mcc-drawer-close" className="text-white/80 hover:text-white"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <nav className="border-b border-zinc-200 px-2 flex gap-0 bg-zinc-50" data-testid="mcc-drawer-tabs">
          {[
            { k: "overview",  label: "Vue d'ensemble" },
            { k: "timeline",  label: "Timeline" },
            { k: "incidents", label: `Incidents (${incidents.length})` },
            { k: "notes",     label: `Notes (${notes.length})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`mcc-tab-${t.k}`}
              className={`px-3 py-2.5 text-[10px] font-mono uppercase tracking-widest border-b-2 transition-colors ${
                tab === t.k ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}>
              {t.label}
            </button>
          ))}
        </nav>

        <div className="p-4 space-y-4 bg-zinc-50">
          {tab === "overview" && (
            <>
              {/* Lazy-loaded map (mounted only while the drawer is open) */}
              <DrawerMap mission={mission} />

              <Section title="🚌 Bus">
                {veh ? (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <KV label="Plaque" value={veh.plate} />
                    <KV label="Modèle" value={veh.model || "—"} />
                    <KV label="Capacité" value={`${veh.capacity || "—"} places`} />
                    <KV label="Tracker GPS" value={veh.navixy_tracker_id || "—"} mono />
                  </div>
                ) : <div className="text-zinc-500 text-xs">Bus non affecté</div>}
              </Section>

              <Section title="👤 Chauffeur">
                {mission.driver ? (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <KV label="Nom" value={mission.driver.name} />
                    <KV label="Email" value={mission.driver.email} mono />
                    <KV label="Téléphone" value={mission.driver.phone || "—"} mono />
                  </div>
                ) : <div className="text-zinc-500 text-xs">Chauffeur non affecté</div>}
              </Section>

              <Section title="📍 GPS Live">
                {mission.gps_position ? (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <KV label="Position" value={`${mission.gps_position.lat?.toFixed(5)}, ${mission.gps_position.lng?.toFixed(5)}`} mono />
                    <KV label="Vitesse" value={`${mission.gps_position.speed_kmh ?? 0} km/h`} />
                    <KV label="Cap" value={`${Math.round(mission.gps_position.heading ?? 0)}°`} />
                    <KV label="Maj GPS" value={mission.gps_position.updated_at?.slice(11, 19) || "—"} mono />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-zinc-500 text-xs"><WifiOff size={12} /> Pas de position GPS</div>
                )}
              </Section>

              <Section title="🚏 Arrêts">
                <div className="space-y-1.5">
                  {(route?.stops || []).map((s, i) => {
                    const passed = i < mission.current_stop_index;
                    const current = i === mission.current_stop_index;
                    return (
                      <div key={s.id || i} className={`flex items-center gap-2 text-xs ${passed ? "opacity-50 line-through" : ""}`} data-testid={`drawer-stop-${i}`}>
                        <span className={`w-6 h-6 flex items-center justify-center text-[10px] font-bold border ${current ? "bg-amber-400 text-zinc-900 border-amber-500" : passed ? "bg-zinc-100 border-zinc-200 text-zinc-400" : "bg-white border-zinc-300 text-zinc-700"}`}>
                          {i + 1}
                        </span>
                        <span className="flex-1 truncate">{s.address}</span>
                        {current && <span className="text-[9px] font-mono text-amber-600 uppercase tracking-widest">EN COURS</span>}
                      </div>
                    );
                  })}
                  {!route?.stops?.length && <div className="text-zinc-500 text-xs">Aucun arrêt défini</div>}
                </div>
              </Section>

              <Section title="🎯 Mission Score">
                {mission.mission_score != null ? (
                  <div className="font-display font-black text-3xl text-emerald-600">{mission.mission_score}/100</div>
                ) : (
                  <div className="text-zinc-500 text-xs">Score calculé après terminaison</div>
                )}
              </Section>
            </>
          )}

          {tab === "timeline" && (
            <Section title="📜 Timeline">
              {timeline.items.length === 0 ? (
                <div className="text-zinc-500 text-xs">Aucun événement.</div>
              ) : (
                <ol className="border-l border-zinc-200 ml-2 space-y-2">
                  {timeline.items.map((it, i) => (
                    <li key={i} className="pl-3 relative" data-testid={`timeline-${it.type}-${i}`}>
                      <span className="absolute -left-1 top-1 w-2 h-2 rounded-full bg-zinc-900" />
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                        {it.ts?.slice(11, 19)} · {it.type}
                      </div>
                      <div className="text-xs text-zinc-800 mt-0.5">{it.label}</div>
                    </li>
                  ))}
                </ol>
              )}
            </Section>
          )}

          {tab === "incidents" && (
            <Section title="⚠ Incidents" actions={
              <button onClick={() => setShowIncidentForm(!showIncidentForm)} data-testid="incident-add-toggle" className="text-[10px] font-mono uppercase tracking-widest text-red-600 hover:text-red-700">
                {showIncidentForm ? "Annuler" : "+ Signaler"}
              </button>
            }>
              {showIncidentForm && (
                <IncidentForm
                  missionId={missionId}
                  onCreated={() => { setShowIncidentForm(false); load(); }}
                />
              )}
              {incidents.length === 0 ? (
                <div className="text-zinc-500 text-xs">Aucun incident sur cette mission.</div>
              ) : (
                <ul className="space-y-2">
                  {incidents.map((inc) => (
                    <li key={inc.id} data-testid={`incident-${inc.id}`} className={`border p-2.5 ${inc.status === "closed" ? "border-zinc-200 bg-zinc-50 opacity-70" : "border-red-200 bg-red-50"}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-red-600">
                            {inc.severity?.toUpperCase()} · {inc.category}
                          </div>
                          <div className="font-bold text-xs">{inc.title}</div>
                          {inc.description && <div className="text-[11px] text-zinc-600 mt-0.5">{inc.description}</div>}
                          <div className="text-[9px] font-mono text-zinc-500 mt-1">{inc.created_at?.slice(0, 19).replace("T", " ")}</div>
                        </div>
                        {inc.status !== "closed" && (
                          <button
                            data-testid={`incident-close-${inc.id}`}
                            onClick={async () => { await api.post(`/missions/${missionId}/incidents/${inc.id}/close`); toast.success("Incident clôturé"); load(); }}
                            className="text-[10px] font-mono uppercase tracking-widest border border-zinc-300 px-2 py-1 hover:bg-white bg-white">
                            Clôturer
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          )}

          {tab === "notes" && (
            <Section title="💬 Notes Dispatch">
              <div className="flex items-end gap-2">
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Ajouter une note pour la mission…"
                  data-testid="dispatch-note-input"
                  className="flex-1 bg-white border border-zinc-300 px-2 py-1.5 text-sm min-h-[60px]" />
                <button onClick={submitNote} data-testid="dispatch-note-submit" className="bg-zinc-900 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 hover:bg-zinc-800">
                  Ajouter
                </button>
              </div>
              <ul className="space-y-2 mt-3">
                {notes.length === 0 ? (
                  <li className="text-zinc-500 text-xs">Aucune note.</li>
                ) : notes.map((n) => (
                  <li key={n.id} data-testid={`note-${n.id}`} className="bg-white border border-zinc-200 p-2">
                    <div className="text-[10px] font-mono text-zinc-500">
                      {n.author_name} · {n.created_at?.slice(0, 19).replace("T", " ")}
                    </div>
                    <div className="text-xs mt-0.5 whitespace-pre-wrap">{n.body}</div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Lifecycle actions */}
          <div className="pt-3 border-t border-zinc-200 flex flex-wrap gap-2" data-testid="drawer-actions">
            {mission.status === "pending" && (
              <button onClick={() => { onStart(mission.id); onAction(); }} data-testid="drawer-start" className="bg-emerald-600 text-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-emerald-500">
                <Play size={11} className="inline mr-1" /> Démarrer la mission
              </button>
            )}
            {mission.status === "active" && (
              <button onClick={() => { onComplete(mission.id); onAction(); }} data-testid="drawer-complete" className="bg-zinc-900 text-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800">
                <CheckCircle2 size={11} className="inline mr-1" /> Terminer la mission
              </button>
            )}
            {mission.driver?.phone && (
              <a href={`tel:${mission.driver.phone}`} data-testid="drawer-call" className="bg-white border border-zinc-300 text-zinc-800 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-50 inline-flex items-center gap-1">
                <Phone size={11} /> Appeler
              </a>
            )}
            <button onClick={() => setTab("timeline")} data-testid="drawer-history" className="bg-white border border-zinc-300 text-zinc-800 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-50 inline-flex items-center gap-1">
              <ListChecks size={11} /> Historique
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Section({ title, children, actions }) {
  return (
    <section className="bg-white border border-zinc-200">
      <header className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 font-bold">{title}</div>
        {actions}
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-xs font-semibold truncate ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function IncidentForm({ missionId, onCreated }) {
  const [form, setForm] = useState({ category: "traffic", title: "", severity: "warning", description: "", priority: "normal" });
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Titre requis");
    try {
      await api.post(`/missions/${missionId}/incident`, form);
      toast.success("Incident créé");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div data-testid="incident-form" className="bg-zinc-50 border border-zinc-200 p-2.5 mb-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <select data-testid="incident-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white border border-zinc-300 px-2 py-1 text-xs font-mono">
          <option value="delay">Retard</option>
          <option value="breakdown">Panne</option>
          <option value="traffic">Trafic</option>
          <option value="driver_absent">Absence chauffeur</option>
          <option value="child_absent">Enfant absent</option>
          <option value="pmr">Problème PMR</option>
          <option value="offline">Véhicule offline</option>
          <option value="other">Autre</option>
        </select>
        <select data-testid="incident-severity" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="bg-white border border-zinc-300 px-2 py-1 text-xs font-mono">
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="critical">Critique</option>
        </select>
      </div>
      <input data-testid="incident-title" placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-white border border-zinc-300 px-2 py-1 text-sm" />
      <textarea data-testid="incident-desc" placeholder="Description (optionnel)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-white border border-zinc-300 px-2 py-1 text-xs" rows={2} />
      <button onClick={submit} data-testid="incident-submit" className="w-full bg-red-600 text-white text-xs font-bold uppercase tracking-widest py-1.5 hover:bg-red-500">
        Créer l&apos;incident
      </button>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────
 * Route-sheets view ("Fiches de route chauffeur")
 *
 * Data is fully derived server-side from planned_services + tour_assignments
 * + vehicles + users — no operator typing required. Each card represents
 * ONE driver's day. Actions: voir, imprimer, télécharger PDF, envoyer (stub).
 * ──────────────────────────────────────────────────────────────────── */
function RouteSheetsView() {
  const today = new Date().toISOString().slice(0, 10);
  const [filters, setFilters] = useState({
    date: today, driver_id: "", vehicle_id: "", tour_id: "", school: "",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [openSheetId, setOpenSheetId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api.get(`/missions/route-sheets?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Fetch drivers & vehicles once for the filter dropdowns
  useEffect(() => {
    (async () => {
      try {
        const [d, v] = await Promise.all([
          api.get("/users?role=driver&active_only=true"),
          api.get("/vehicles"),
        ]);
        setDrivers(d.data || []);
        setVehicles(Array.isArray(v.data) ? v.data : (v.data?.items || []));
      } catch { /* silent */ }
    })();
  }, []);

  return (
    <section className="bg-white border border-zinc-200" data-testid="route-sheets-view">
      {/* Filters bar */}
      <div className="border-b border-zinc-200 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="rs-filters">
        <CalIcon size={13} className="text-zinc-500" />
        <input
          type="date"
          value={filters.date}
          onChange={(e) => setFilters({ ...filters, date: e.target.value })}
          data-testid="rs-filter-date"
          className="border border-zinc-300 px-2 py-1 text-xs font-mono bg-white"
        />
        <select
          value={filters.driver_id}
          onChange={(e) => setFilters({ ...filters, driver_id: e.target.value })}
          data-testid="rs-filter-driver"
          className="border border-zinc-300 px-2 py-1 text-xs bg-white min-w-[160px]">
          <option value="">— Tous chauffeurs —</option>
          {drivers.map((d) => <option key={d.id} value={d.id}>{d.name || d.email}</option>)}
        </select>
        <select
          value={filters.vehicle_id}
          onChange={(e) => setFilters({ ...filters, vehicle_id: e.target.value })}
          data-testid="rs-filter-vehicle"
          className="border border-zinc-300 px-2 py-1 text-xs bg-white min-w-[160px]">
          <option value="">— Tous véhicules —</option>
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.model || v.label || v.plate}</option>)}
        </select>
        <input
          type="text"
          placeholder="Tournée (ex. 5101)"
          value={filters.tour_id}
          onChange={(e) => setFilters({ ...filters, tour_id: e.target.value })}
          data-testid="rs-filter-tour"
          className="border border-zinc-300 px-2 py-1 text-xs font-mono bg-white w-[150px]"
        />
        <input
          type="text"
          placeholder="École / client"
          value={filters.school}
          onChange={(e) => setFilters({ ...filters, school: e.target.value })}
          data-testid="rs-filter-school"
          className="border border-zinc-300 px-2 py-1 text-xs bg-white w-[160px]"
        />
        <div className="flex-1" />
        <span className="text-[11px] text-zinc-500">
          {loading ? "Chargement…" : `${items.length} chauffeur(s)`}
        </span>
        <BulkZipButton filters={filters} count={items.length} />
        <button
          onClick={load}
          data-testid="rs-refresh"
          className="px-2 py-1 border border-zinc-900 text-[10px] font-mono uppercase tracking-widest hover:bg-zinc-900 hover:text-white inline-flex items-center gap-1">
          <RefreshCw size={11} /> Rafraîchir
        </button>
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="p-8 text-center text-zinc-400 text-sm" data-testid="rs-loading">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Chargement des fiches…
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-zinc-500 text-sm" data-testid="rs-empty">
          <FileText size={20} className="inline mr-2 text-zinc-400" />
          Aucune fiche de route pour cette date avec ces filtres.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200" data-testid="rs-grid">
          {items.map((s) => (
            <RouteSheetCard key={s.driver_id} sheet={s}
              onOpen={() => setOpenSheetId(s.driver_id)} />
          ))}
        </div>
      )}

      {/* Detail modal */}
      {openSheetId && (
        <RouteSheetModal
          driverId={openSheetId}
          date={filters.date}
          onClose={() => setOpenSheetId(null)}
        />
      )}
    </section>
  );
}

function BulkZipButton({ filters, count }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (busy || count === 0) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters.date)       params.set("date", filters.date);
      if (filters.driver_id)  params.set("driver_id", filters.driver_id);
      if (filters.vehicle_id) params.set("vehicle_id", filters.vehicle_id);
      if (filters.tour_id)    params.set("tour_id", filters.tour_id);
      if (filters.school)     params.set("school", filters.school);
      const url = `/missions/route-sheets/zip?${params.toString()}`;
      const r = await api.get(url, { responseType: "blob" });
      const packed = Number(r.headers?.["x-sheets-count"] || 0);
      const failed = Number(r.headers?.["x-sheets-failed"] || 0);
      const blob = new Blob([r.data], { type: "application/zip" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fiches-route_${filters.date || "today"}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      if (failed > 0) {
        toast.warning(`${packed} fiche(s) téléchargée(s), ${failed} en échec — voir _manifest.json`);
      } else {
        toast.success(`${packed} fiche(s) téléchargée(s) dans le ZIP`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Échec du téléchargement en lot");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={onClick}
      disabled={busy || count === 0}
      data-testid="rs-bulk-zip"
      title={count === 0 ? "Aucune fiche à télécharger" : `Télécharger ${count} fiche(s) en ZIP`}
      className="px-2 py-1 bg-[#FFC60A] text-zinc-900 border border-zinc-900 text-[10px] font-mono font-black uppercase tracking-widest hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
      {busy ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
      Tout télécharger {count > 0 ? `(${count})` : ""}
    </button>
  );
}

function RouteSheetCard({ sheet, onOpen }) {
  const [busy, setBusy] = useState(false);
  const downloadPdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = `/missions/route-sheets/${sheet.driver_id}/pdf?date=${sheet.date}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fiche-route_${sheet.driver_name.replace(/\s+/g, "-")}_${sheet.date}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success("Fiche téléchargée");
    } catch {
      toast.error("Échec du téléchargement");
    } finally {
      setBusy(false);
    }
  };
  const sendStub = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/missions/route-sheets/${sheet.driver_id}/send?date=${sheet.date}`);
      toast.success(r.data?.message || "Envoi simulé");
    } catch {
      toast.error("Échec de l'envoi");
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="bg-white p-3" data-testid={`rs-card-${sheet.driver_id}`}>
      <header className="flex items-start justify-between gap-2 pb-2 border-b border-zinc-200 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <User size={12} className="text-zinc-500" />
            <span className="font-bold text-sm truncate" data-testid={`rs-driver-name-${sheet.driver_id}`}>
              {sheet.driver_name}
            </span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {sheet.weekday} {sheet.date_fmt}
            {sheet.driver_phone ? <span className="ml-1.5">· {sheet.driver_phone}</span> : null}
          </div>
        </div>
        <span className="px-1.5 py-0.5 bg-[#FFC60A] text-zinc-900 text-[10px] font-mono font-bold border border-zinc-900 uppercase tracking-widest">
          {sheet.tours_count} tournée{sheet.tours_count > 1 ? "s" : ""}
        </span>
      </header>
      <dl className="text-[11px] grid grid-cols-2 gap-y-1 mb-2">
        <dt className="text-zinc-500">Véhicule</dt>
        <dd className="text-right font-mono">{sheet.vehicles[0] || "—"}</dd>
        <dt className="text-zinc-500">Départ</dt>
        <dd className="text-right font-mono">{sheet.earliest_start || "—"}</dd>
        <dt className="text-zinc-500">Retour</dt>
        <dd className="text-right font-mono">{sheet.latest_end || "—"}</dd>
        <dt className="text-zinc-500">Tournées</dt>
        <dd className="text-right font-mono truncate">{sheet.tours_preview.join(", ")}</dd>
      </dl>
      <div className="flex items-center gap-1 pt-1 border-t border-zinc-100">
        <button
          onClick={onOpen}
          data-testid={`rs-view-${sheet.driver_id}`}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 hover:bg-zinc-900 hover:text-[#FFC60A]">
          <Eye size={11} /> Voir
        </button>
        <button
          onClick={downloadPdf}
          disabled={busy}
          data-testid={`rs-pdf-${sheet.driver_id}`}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 hover:bg-zinc-900 hover:text-[#FFC60A] disabled:opacity-50">
          <Download size={11} /> PDF
        </button>
        <button
          onClick={() => window.print()}
          data-testid={`rs-print-${sheet.driver_id}`}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 hover:bg-zinc-900 hover:text-[#FFC60A]">
          <Printer size={11} /> Imprimer
        </button>
        <button
          onClick={sendStub}
          disabled={busy}
          data-testid={`rs-send-${sheet.driver_id}`}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 hover:bg-zinc-900 hover:text-[#FFC60A] disabled:opacity-50">
          <Send size={11} /> Envoyer
        </button>
      </div>
    </article>
  );
}

function RouteSheetModal({ driverId, date, onClose }) {
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/missions/route-sheets/${driverId}?date=${date}`);
        setSheet(r.data);
      } catch {
        toast.error("Erreur de chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, [driverId, date]);

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/missions/route-sheets/${driverId}/pdf?date=${date}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fiche-route_${(sheet?.driver_name || "chauffeur").replace(/\s+/g, "-")}_${date}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end print:static print:bg-white print:items-start"
         onClick={onClose} data-testid="rs-modal">
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto print:max-w-none print:w-full print:overflow-visible"
           onClick={(e) => e.stopPropagation()}>
        {/* Toolbar (hidden when printing) */}
        <div className="sticky top-0 z-10 bg-zinc-900 text-white px-4 py-2 flex items-center justify-between border-b-2 border-[#FFC60A] print:hidden">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-[#FFC60A]" />
            <span className="font-display font-bold text-sm">Fiche de route</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => window.print()} data-testid="rs-modal-print"
              className="px-2 py-1 border border-white/30 text-[10px] uppercase tracking-widest hover:bg-white/10 inline-flex items-center gap-1">
              <Printer size={11} /> Imprimer
            </button>
            <button onClick={downloadPdf} data-testid="rs-modal-pdf"
              className="px-2 py-1 bg-[#FFC60A] text-zinc-900 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1">
              <Download size={11} /> PDF
            </button>
            <button onClick={onClose} data-testid="rs-modal-close"
              className="ml-1 p-1 hover:bg-white/10">
              <X size={14} />
            </button>
          </div>
        </div>
        {loading || !sheet ? (
          <div className="p-8 text-center text-zinc-400">
            <Loader2 size={20} className="animate-spin inline" />
          </div>
        ) : (
          <RouteSheetPrintable sheet={sheet} />
        )}
      </div>
    </div>
  );
}

function RouteSheetPrintable({ sheet }) {
  return (
    <div className="p-6 print:p-0" data-testid="rs-printable">
      {/* Heading band */}
      <div className="bg-zinc-900 text-white p-4 flex items-center justify-between mb-1 print:bg-zinc-900 print:text-white">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A]">FICHE DE ROUTE</div>
          <div className="text-2xl font-display font-black mt-0.5">{sheet.driver_name}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{sheet.weekday}</div>
          <div className="text-2xl font-mono font-bold">{sheet.date_fmt}</div>
        </div>
      </div>

      {/* Info strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border-b-2 border-zinc-900 mb-3">
        <InfoCell label="Véhicule" value={sheet.vehicle_label} />
        <InfoCell label="Téléphone chauffeur" value={sheet.driver_phone} />
        <InfoCell label="Téléphone exploitation" value={sheet.phone_dispatch} mono />
        <InfoCell label="Tournées" value={`${sheet.tours_count} (${sheet.earliest_start || "—"} → ${sheet.latest_end || "—"})`} />
      </div>

      {sheet.empty && (
        <div className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-sm" data-testid="rs-empty-sheet">
          Aucune tournée planifiée ce jour pour ce chauffeur.
        </div>
      )}

      {/* Each tour */}
      {(sheet.tours || []).map((tour) => (
        <div key={tour.planned_service_id} className="mb-4 break-inside-avoid" data-testid={`rs-tour-${tour.tour_id}`}>
          <div className="bg-[#FFC60A] px-3 py-1.5 flex items-center justify-between border border-zinc-900">
            <div className="flex items-center gap-2">
              <span className="font-mono font-black text-base">{tour.tour_code}</span>
              <span className="text-xs">{tour.day_name}</span>
              {tour.client_name && <span className="text-xs text-zinc-700">· {tour.client_name}</span>}
              {tour.is_override && (
                <span className="ml-1 px-1.5 py-0.5 bg-zinc-900 text-[#FFC60A] text-[9px] font-bold uppercase tracking-widest">Exception</span>
              )}
            </div>
            <div className="text-xs font-mono">
              Départ <strong>{tour.start_time || "—"}</strong>
              <span className="ml-2">Retour <strong>{tour.end_time || "—"}</strong></span>
            </div>
          </div>
          <table className="w-full text-xs border border-t-0 border-zinc-900">
            <thead className="bg-zinc-900 text-white">
              <tr>
                <th className="px-2 py-1 text-left font-mono text-[10px] uppercase tracking-widest w-10">#</th>
                <th className="px-2 py-1 text-left font-mono text-[10px] uppercase tracking-widest w-20">Heure</th>
                <th className="px-2 py-1 text-left font-mono text-[10px] uppercase tracking-widest">Arrêt</th>
                <th className="px-2 py-1 text-center font-mono text-[10px] uppercase tracking-widest w-20">Élèves</th>
                <th className="px-2 py-1 text-left font-mono text-[10px] uppercase tracking-widest w-48">Remarque</th>
              </tr>
            </thead>
            <tbody>
              {(tour.items || []).map((it, idx) => {
                const arr = (it.arrival || "").slice(0, 5);
                const dep = (it.departure || "").slice(0, 5);
                const timeStr = arr === dep ? arr : `${arr}→${dep}`;
                const students = [it.pickup ? `+${it.pickup}` : null, it.dropoff ? `-${it.dropoff}` : null].filter(Boolean).join(" ");
                return (
                  <tr key={idx} className={`border-t border-zinc-200 ${idx % 2 ? "bg-zinc-50" : "bg-white"} ${it.kind === "event" ? "italic text-zinc-600" : ""}`}>
                    <td className="px-2 py-1 text-center font-mono">{idx + 1}</td>
                    <td className="px-2 py-1 font-mono">{timeStr || "—"}</td>
                    <td className="px-2 py-1">
                      {it.kind === "event" && <span className="text-zinc-400 mr-1">⏸</span>}
                      {it.name}
                    </td>
                    <td className="px-2 py-1 text-center font-mono">{students}</td>
                    <td className="px-2 py-1 text-zinc-600 text-[11px]">{it.remark || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {tour.remark && (
            <div className="text-[11px] italic text-zinc-600 px-3 py-1 border border-t-0 border-zinc-900 bg-amber-50">
              Note : {tour.remark}
            </div>
          )}
        </div>
      ))}

      {/* Signature zone */}
      {!sheet.empty && (
        <div className="grid grid-cols-2 gap-2 mt-6 break-inside-avoid" data-testid="rs-signature-zone">
          <div className="border border-zinc-900 p-2 min-h-[80px]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Remarques journée</div>
          </div>
          <div className="border border-zinc-900 p-2 min-h-[80px]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Signature chauffeur</div>
          </div>
        </div>
      )}
      <div className="text-[10px] text-zinc-400 mt-3 text-center print:mt-1">
        Document généré automatiquement par {sheet.tenant_name || "Logibus"} depuis l&apos;import Excel + affectations.
        En cas de problème, appeler l&apos;exploitation au <strong>{sheet.phone_dispatch || "—"}</strong>.
      </div>
    </div>
  );
}

function InfoCell({ label, value, mono }) {
  return (
    <div className="bg-zinc-50 p-2">
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
